import { useEffect, useState, useCallback, useMemo } from "react";
import { PageTitle } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useCachedState, cacheGet } from "@/lib/swr";
import { toast } from "sonner";
import { MessageSquareHeart, Users } from "lucide-react";
import { useDash } from "./ctx";
import { Toolbar } from "./feedback/Toolbar";
import { FeedbackList } from "./feedback/FeedbackList";
import { FeedbackDetail } from "./feedback/FeedbackDetail";
import { groupFeedback, isDesktop } from "./feedback/utils";
import { V } from "@/lib/vocab";

const PAGE = 100;
const PLATFORM_ROLES = ["super_admin", "platform_admin", "onboarding_admin"];

export default function Feedback() {
  // Two contexts: super-admins get the cross-community feed (/admin/feedback); community
  // managers get their community's own feedback (/tenants/{tid}/feedback). Same UI.
  const { tenantId, role, tenant } = useDash();
  const isPlatformView = PLATFORM_ROLES.includes(role) && !tenantId;
  const listUrl = isPlatformView ? "/admin/feedback" : (tenantId ? `/tenants/${tenantId}/feedback` : null);
  const bulkUrl = `${listUrl}/bulk`;

  const [scope, setScope] = useState(isPlatformView ? "platform" : "community");
  const [box, setBox] = useState("new"); // Inbox (new) · Done (reviewed + legacy archived)
  const [query, setQuery] = useState("");
  const cacheKey = `feedback:${listUrl}:${scope}:${box}`;
  const [items, setItems] = useCachedState(cacheKey, []);
  const [counts, setCounts] = useCachedState(`feedback-counts:${listUrl}:${scope}`, {});
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(() => cacheGet(cacheKey) === undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [mobileDetail, setMobileDetail] = useState(false);

  const fetchPage = useCallback((offset) => {
    const params = new URLSearchParams({ status: box, kind: scope, limit: String(PAGE), offset: String(offset) });
    return api.get(`${listUrl}?${params.toString()}`);
  }, [listUrl, scope, box]);

  const load = useCallback(() => {
    if (!listUrl) return;
    fetchPage(0)
      .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); setTotal(r.data.total ?? null); })
      .catch(() => toast.error("Couldn't load feedback"))
      .finally(() => setLoading(false));
  }, [listUrl, fetchPage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(cacheGet(cacheKey) === undefined); load(); }, [load, cacheKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await fetchPage(items.length);
      setItems([...items, ...(r.data.items || [])]);
      setTotal(r.data.total ?? total);
    } catch { toast.error("Couldn't load more"); }
    setLoadingMore(false);
  };

  const groups = useMemo(() => groupFeedback(items), [items]);
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => !q ? groups : groups.filter((g) =>
    g.items.some((i) => `${i.message} ${i.verbatim || ""} ${i.name || ""} ${i.phone || ""} ${i.email || ""} ${i.community || ""}`.toLowerCase().includes(q))), [groups, q]);
  const selected = shown.find((g) => g.id === selectedId) || (isDesktop() ? shown[0] : null) || null;

  const switchBox = (k) => { setBox(k); setSelectedId(null); setMobileDetail(false); };
  const switchScope = (k) => { setScope(k); setSelectedId(null); setMobileDetail(false); };
  const select = (g) => { setSelectedId(g.id); setMobileDetail(true); };
  const bump = () => { load(); window.dispatchEvent(new CustomEvent("askredline:counts-changed")); };

  const setStatus = async (group, next) => {
    const idx = shown.findIndex((g) => g.id === group.id);
    const nxt = shown[idx + 1] || shown[idx - 1] || null;
    setBusy(true);
    try {
      await api.put(bulkUrl, { ids: group.ids, status: next });
      setItems(items.filter((i) => !group.ids.includes(i.id)));
      setSelectedId(nxt ? nxt.id : null);
      if (!nxt) setMobileDetail(false);
      toast.success(next === "new" ? "Back in the inbox" : group.ids.length > 1 ? `${group.ids.length} marked done` : "Marked done");
      bump();
    } catch { toast.error("Couldn't update"); }
    setBusy(false);
  };

  const markAllDone = async () => {
    setBusy(true);
    try {
      const r = await api.put(bulkUrl, { all: true, kind: scope, from_status: "new", status: "reviewed" });
      setItems([]); setSelectedId(null); setMobileDetail(false);
      toast.success(`${r.data.updated} marked done`);
      bump();
    } catch { toast.error("Couldn't update"); }
    setBusy(false);
  };

  // Inbox keys: ↑ ↓ move, E marks done / reopens. Ignored while typing.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (!shown.length) return;
      const idx = shown.findIndex((g) => g.id === (selected && selected.id));
      if (e.key === "ArrowDown") { e.preventDefault(); setSelectedId(shown[Math.min(idx + 1, shown.length - 1)].id); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedId(shown[Math.max(idx - 1, 0)].id); }
      else if ((e.key === "e" || e.key === "E") && selected && !busy) setStatus(selected, box === "new" ? "reviewed" : "new");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const place = isPlatformView ? `every ${V.lc("org")}` : ((tenant && tenant.name) || `your ${V.lc("org")}`);

  return (
    <div className="space-y-5">
      <div>
        <PageTitle title={<><MessageSquareHeart className="h-7 w-7 text-redline-500" strokeWidth={1.75} /> {isPlatformView ? "Platform Feedback" : `${V.one("org")} Feedback`}</>} about={<>{isPlatformView
            ? `What people tell the assistant about Redline itself, from every ${V.lc("org")}. Read it, act on it, mark it done.`
            : `What ${V.lcs("person")} tell the concierge about ${place}. Read it, act on it, mark it done.`}</>} />
      </div>

      <Toolbar box={box} onBox={switchBox} counts={counts} query={query} onQuery={setQuery} scope={scope} onScope={switchScope}
        isPlatformView={isPlatformView} canMarkAll={box === "new" && items.length > 0 && !busy} onMarkAll={markAllDone} />

      {isPlatformView && scope === "community" && (
        <div data-testid="feedback-scope-note" className="flex items-center gap-2 rounded-2xl border border-sand-200 bg-sand-50 px-4 py-2.5 text-sm text-ink/70">
          <Users className="h-4 w-4 shrink-0" />
          Feedback {V.lcs("person")} gave about their own {V.lcs("org")} — each {V.lc("org")}'s manager handles these.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,400px)_1fr] gap-5 items-start">
        <div className={`min-w-0 ${mobileDetail ? "hidden lg:block" : "block"}`}>
          <div className="text-xs text-muted-foreground mb-2 px-1 flex items-center gap-2" data-testid="feedback-result-count">
            <span>{shown.length} {shown.length === 1 ? "entry" : "entries"}{q ? " matching" : box === "new" ? " waiting" : " handled"}</span>
            {q && total != null && items.length < total && <span className="text-ink/40">· searching the {items.length} loaded — load older to widen</span>}
          </div>
          <FeedbackList groups={shown} selectedId={selected && selected.id} onSelect={select} isPlatformView={isPlatformView} box={box} query={q}
            counts={counts} onBox={switchBox} total={total} loaded={items.length} onLoadMore={loadMore} loadingMore={loadingMore} loading={loading} />
        </div>

        <div data-testid="feedback-detail-panel" className={`min-w-0 bg-white border-sand-200 overflow-hidden flex flex-col ${
          mobileDetail
            ? "fixed inset-0 z-[60] h-[100dvh] rounded-none lg:static lg:z-auto lg:h-[72vh] lg:rounded-2xl lg:border"
            : "hidden lg:flex h-[72vh] rounded-2xl border"}`}>
          <FeedbackDetail group={selected} onStatus={setStatus} busy={busy} isPlatformView={isPlatformView} mobile={mobileDetail} onBack={() => setMobileDetail(false)}
            copilotOn={!isPlatformView && !!(tenant && tenant.copilot_enabled)} />
        </div>
      </div>
    </div>
  );
}
